import type { UIPartyCharacter } from '@sorrem-client/types';
import { AsyncStateMessage } from '@sorrem-client/ui/components';
import { SkillTree, SkillTreeBackground } from '@sorrem-client/ui/features/SkillTree';
import { formatAssetPrecache, useAssetPrecache, useDisplaySettings } from '@sorrem-client/ui/hooks';
import type { ValueStore } from '@sorrem-client/ui/shared/value-store';
import { getWieldedLoadout, resolveRecentTraining } from '@sorrem/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CharacterCreationDraft, SkillsTabScene } from '@sorrem-client/ui/features/CharacterHome/hooks';
import type { CharacterHomeTab } from '@sorrem-client/ui/features/CharacterHome/tabs';
import { hasEnteredHomeThisSession, markHomeEnteredThisSession } from '@sorrem-client/ui/features/CharacterHome/home-entry';
import { CharacterHomeViewport } from '../CharacterHomeViewport';
import styles from './CharacterHomeBackdrop.module.scss';
import { resolveViewportProps } from './resolve-viewport-props';

/** Seeds the sky's motes while a character is being created and there is no character id yet. */
const CHARACTER_CREATION_SKY_SEED = 'character-creation';

interface CharacterHomeBackdropProps {
    readonly player: UIPartyCharacter | null;
    readonly isCreationViewShown: boolean;
    readonly draft: CharacterCreationDraft;
    readonly isSkillsTab: boolean;
    readonly isJourneyTab: boolean;
    /** The tab under the pointer; its prop in the room highlights. */
    readonly hoveredTabStore: ValueStore<CharacterHomeTab | null>;
    readonly skillsScene: SkillsTabScene;
    /** The selected skill or dial (`dial:<branch>`). */
    readonly selectedSkillTreeId: string | null;
    readonly onSelectSkillTreeId: (id: string | null) => void;
    /** Told when the loading message covering the home comes and goes. */
    readonly onLoadingChange?: (isLoading: boolean) => void;
}

/**
 * Everything behind Character Home's panels: the Veil sky (seen through the
 * home's window), the home with the character in it, the full-screen skill
 * tree, and the loading message until the home and the sky are drawn and
 * the core assets are cached (#285), with how many are left; the rest cache
 * in the background after, with a quiet count in a corner.
 */
export function CharacterHomeBackdrop({
    player,
    isCreationViewShown,
    draft,
    isSkillsTab,
    isJourneyTab,
    hoveredTabStore,
    skillsScene,
    selectedSkillTreeId,
    onSelectSkillTreeId,
    onLoadingChange,
}: CharacterHomeBackdropProps): JSX.Element {
    const { characterHomeEffects } = useDisplaySettings();
    // The home and the character are 3D and move only at "All"; the sky and the tree are 2D and move at "Minimal" too.
    const animates3d = characterHomeEffects === 'all';
    const animates2d = characterHomeEffects !== 'off';
    // Link pulses: praxis gained over the last few runs, newer weighted more.
    const recentTraining = useMemo(() => resolveRecentTraining(player?.recentRunPraxis ?? []), [player?.recentRunPraxis]);
    // The tree judges which skills are active on the gear as wielded; kept stable so it only rebuilds when the character changes.
    const wieldedLoadout = useMemo(() => (player === null ? null : getWieldedLoadout(player)), [player]);
    // Fires once, the first time the persistent home viewport (room +
    // character) has finished loading - see CharacterHomeViewport/
    // CharacterPreviewCanvas's `onReady`. Gates the loading message below.
    const [isSceneReady, setSceneReady] = useState(false);
    // The first time home is reached after login the player is entering it;
    // after that (back from a run, say) they are returning.
    const [isFirstEntry] = useState(() => !hasEnteredHomeThisSession());
    const handleSceneReady = useCallback((): void => {
        setSceneReady(true);
        markHomeEnteredThisSession();
    }, []);

    // Entering waits for the core assets (#285); the run tier caches in the background after.
    const precache = useAssetPrecache();
    const isCachingCore = precache.phase === 'idle' || precache.phase === 'core';
    const isLoading = !isSceneReady || !skillsScene.isSkyReady || isCachingCore;
    useEffect(() => {
        onLoadingChange?.(isLoading);
    }, [isLoading, onLoadingChange]);

    const viewport = resolveViewportProps({
        isCreationViewShown,
        draft,
        player,
        animationsEnabled: animates3d,
        isHomeHidden: isSkillsTab || isJourneyTab,
    });

    return (
        <>
            {/* Behind the home, seen through its window; the motes only drift while Skills shows it. */}
            <SkillTreeBackground
                seed={player?.id ?? CHARACTER_CREATION_SKY_SEED}
                viewStore={skillsScene.viewStore}
                animationsEnabled={animates2d && skillsScene.isBackdropShown}
                isVisible
                onReady={skillsScene.onSkyReady}
            />

            <CharacterHomeViewport
                {...viewport}
                highlightedTabStore={hoveredTabStore}
                highlightsTabs={!isCreationViewShown}
                zoomedIntoWindow={skillsScene.isBackdropShown}
                onZoomedIntoWindowChange={skillsScene.setIsZoomedIntoWindow}
                zoomedIntoTable={isJourneyTab}
                onReady={handleSceneReady}
            />

            {/* Full screen, above the sky and below the panels, which float over it. */}
            {player !== null && wieldedLoadout !== null && skillsScene.hasVisitedSkills && (
                <SkillTree
                    characterId={player.id}
                    skills={player.skills}
                    equipped={wieldedLoadout}
                    disciplines={player.disciplines}
                    recentTraining={recentTraining}
                    selectedId={selectedSkillTreeId}
                    onSelect={onSelectSkillTreeId}
                    viewStore={skillsScene.viewStore}
                    isVisible={isSkillsTab && skillsScene.isSkyReady && skillsScene.isZoomedIntoWindow}
                    animationsEnabled={animates2d}
                    frameRegionRef={skillsScene.mainRegionRef}
                    onShownChange={skillsScene.setIsTreeShown}
                />
            )}

            {isLoading && (
                <div className={styles.sceneLoading}>
                    <AsyncStateMessage
                        isLoading
                        loadingMessage={isFirstEntry ? 'Entering Sorrem...' : 'Returning home...'}
                    />
                    {precache.phase === 'core' && precache.total > 0 && (
                        <p className={styles.caching}>{formatAssetPrecache(precache)}</p>
                    )}
                </div>
            )}

            {!isLoading && precache.phase === 'run' && (
                <p className={styles.backgroundCaching}>{formatAssetPrecache(precache)}</p>
            )}
        </>
    );
}
